import React from 'react';
import {
  Alert,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CohortExecutionPanel from './CohortExecutionPanel';
import CohortQueryPanel from './CohortQueryPanel';
import CohortToolbarActions from './CohortToolbarActions';

const CohortToolbar = ({
  nodesCount,
  isExecuting,
  breakpointNodeId,
  breakpointNodeLabel,
  historyPastLength,
  historyFutureLength,
  layoutDirection,
  viewMode,
  hasActiveCohort,
  graphStatistics,
  lastExecutionLabel,
  executionProgress,
  executionTargetNodeId,
  executionMode,
  executionBackend,
  onSetExecutionBackend,
  showExecutionLog,
  executionLogs,
  queryResult,
  queryError,
  isLoadingQuery,
  queryBackend,
  onSetQueryBackend,
  onShowQuery,
  onDownloadQuery,
  graphLastError,
  onCreateRootNode,
  onLoadDemo,
  onGenerateLargeDemo,
  onAutoLayout,
  onExecuteGraph,
  onExecuteToBreakpoint,
  onSaveGraph,
  onImportJson,
  onExportJson,
  onExportPng,
  onExportSvg,
  onClearGraph,
  onUndo,
  onRedo,
  onOpenShortcuts,
  onToggleExecutionLog,
  onSetLayoutDirection,
  onSetViewMode,
}) => (
  <Paper sx={{ p: { xs: 1.5, md: 2 }, mb: 2 }}>
    <Stack spacing={1.25}>
      <Typography variant="body2" color="text.secondary">
        Tip: Start from the saved cohort list, then open a cohort in graph view to edit and save it back to the database.
      </Typography>
      <CohortToolbarActions
        nodesCount={nodesCount}
        isExecuting={isExecuting}
        breakpointNodeId={breakpointNodeId}
        breakpointNodeLabel={breakpointNodeLabel}
        historyPastLength={historyPastLength}
        historyFutureLength={historyFutureLength}
        layoutDirection={layoutDirection}
        viewMode={viewMode}
        hasActiveCohort={hasActiveCohort}
        showExecutionLog={showExecutionLog}
        onCreateRootNode={onCreateRootNode}
        onLoadDemo={onLoadDemo}
        onGenerateLargeDemo={onGenerateLargeDemo}
        onAutoLayout={onAutoLayout}
        onExecuteGraph={onExecuteGraph}
        onExecuteToBreakpoint={onExecuteToBreakpoint}
        onSaveGraph={onSaveGraph}
        onImportJson={onImportJson}
        onExportJson={onExportJson}
        onExportPng={onExportPng}
        onExportSvg={onExportSvg}
        onClearGraph={onClearGraph}
        onUndo={onUndo}
        onRedo={onRedo}
        onOpenShortcuts={onOpenShortcuts}
        onToggleExecutionLog={onToggleExecutionLog}
        onSetLayoutDirection={onSetLayoutDirection}
        onSetViewMode={onSetViewMode}
      />
    </Stack>

    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 1.25, flexWrap: 'wrap' }}>
      <Typography variant="caption" color="text.secondary">
        Nodes: <strong>{graphStatistics.totalNodes}</strong>
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Max depth: <strong>{graphStatistics.maxDepth}</strong>
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Total patients: <strong>{graphStatistics.totalPatients.toLocaleString()}</strong>
      </Typography>
      <Typography variant="caption" color={breakpointNodeId ? 'warning.dark' : 'text.secondary'}>
        Breakpoint: <strong>{breakpointNodeLabel || 'None'}</strong>
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Last execution: <strong>{lastExecutionLabel}</strong>
      </Typography>
    </Stack>

    <CohortExecutionPanel
      isExecuting={isExecuting}
      executionTargetNodeId={executionTargetNodeId}
      executionMode={executionMode}
      executionProgress={executionProgress}
      executionBackend={executionBackend}
      onSetExecutionBackend={onSetExecutionBackend}
      showExecutionLog={showExecutionLog}
      executionLogs={executionLogs}
    />

    {/* #796: Die Abfrage haengt bewusst nicht am Ausfuehren -- der Endpunkt
        uebersetzt nur und beruehrt keine Zieldatenbank. Auf hospital1
        antwortet `execute` derzeit mit 503 (keine OMOP-Quelle); anzeigen und
        herunterladen geht trotzdem. Das Backend ist deshalb ein eigener
        Umschalter, nicht der von `executionBackend` -- der laesst `openehr`
        bis #E7.5 gar nicht erst zu (Review Runde 1, B1). */}
    <CohortQueryPanel
      hasActiveCohort={hasActiveCohort}
      queryBackend={queryBackend}
      onSetQueryBackend={onSetQueryBackend}
      queryResult={queryResult}
      queryError={queryError}
      isLoadingQuery={isLoadingQuery}
      onShowQuery={onShowQuery}
      onDownloadQuery={onDownloadQuery}
    />

    {graphLastError ? (
      <Alert severity="error" sx={{ mt: 1 }}>
        {graphLastError}
      </Alert>
    ) : null}
  </Paper>
);

export default CohortToolbar;
